import React, { useContext, useEffect, useState } from "react";
import { Formik } from "formik";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import _ from "lodash";
import upperCase from "lodash/upperCase";
import lowerCase from "lodash/lowerCase";

import { Spin, message } from "antd";

import Form from "./forms";
import { Context } from "../../../../../context";
import { CREATE_PAGE, CREATE_CONTENT, GET_PAGE, REMOVE_MULTI_CONTAINER } from "../../../graphql";

import { validationSchema } from "./constant";

interface Props {
    handleRefresh: () => void;
}

const PrivacyPolicyForms: React.FC<Props> = ({ handleRefresh }) => {
    const { id } = useParams();

    const {
        state: { pageDetails },
    } = useContext(Context);

    const [createPage] = useMutation(CREATE_PAGE);
    const [getPage, { data: pageData }] = useLazyQuery(GET_PAGE, {
        fetchPolicy: "no-cache",
    });
    const [createContent] = useMutation(CREATE_CONTENT);
    const [removeMultiContainer] = useMutation(REMOVE_MULTI_CONTAINER);

    const [loader, setLoader] = useState(false);
    const [reinitialize, setReinitialize] = useState(false);
    const [fieldInfo, setFieldInfo] = useState<any>(null);
    const [initalValue, setInitialValue] = useState<object>({
        title: "",
        body: "",
    });

    useEffect(() => {
        if (!reinitialize) {
            setReinitialize(false);
        }
    }, [reinitialize]);

    useEffect(() => {
        if (pageData && !_.isEmpty(pageData.getSpecificBrandPage)) {
            const data = pageData.getSpecificBrandPage.containers[0].field_collections;
            const containers = pageData.getSpecificBrandPage.containers
                ? pageData.getSpecificBrandPage.containers
                : null;
            const containerIds = containers ? containers.map(({ id }) => id) : [];
            let fields: any = {
                title: "",
                body: "",
            };

            if (data[0].fields) {
                fields["title"] = data[0].fields[0].value;
                fields["body"] = data[0].fields[1].value;
            }

            setFieldInfo({
                pageId: pageData.getSpecificBrandPage.id,
                containerId: containerIds,
                title: fields.title,
                body: fields.body,
            });
            setInitialValue(fields);
            setReinitialize(true);
        }
    }, [pageData]);

    useEffect(() => {
        const data = pageDetails.payload;
        if (data.brandId) {
            getPage({
                variables: {
                    brand_id: data.brandId,
                    tag: `COMPANY-INFO-${upperCase(data.brandName)}`,
                    slug: `privacy-policy-${lowerCase(data.brandName)}`,
                },
            });
        }
    }, [pageDetails.payload, getPage]);

    const handleSubmit = async (values) => {
        setLoader(true);

        const data = pageDetails.payload;

        if (data.brandId && data.brandName && fieldInfo) {
            await handleUpdatePage(values);
        } else {
            await handleAddPage(values);
        }

        setTimeout(() => {
            handleRefresh();
            setLoader(false);
        }, 500);
    };

    const handleAddPage = async (values) => {
        const brandInfo = pageDetails.payload;
        const pageTag = `COMPANY-INFO-${upperCase(brandInfo.brandName)}`;
        const page = await createPage({
            variables: {
                brand_id: id,
                name: `Privacy Policy - ${brandInfo.brandName}`,
                tag: pageTag,
            },
        });

        if (page) {
            const {
                addOrUpdateBrandPage: { id: page_id },
            } = page.data;

            const containerData = {
                page_id,
                name: `Privacy Policy Container - ${brandInfo.brandName}`,
                order: 0,
                field_collections: [
                    {
                        name: "Privacy Policy FC",
                        order: 0,
                        fields: [
                            {
                                order: 0,
                                label: "privacy policy (default)",
                                name: "PRIVACY-POLICY-TITLE",
                                value: values.title ? values.title.trim() : "",
                            },
                            {
                                order: 1,
                                label: "body",
                                name: "BODY",
                                value: values.body ? values.body.trim() : "",
                            },
                        ],
                    },
                ],
            };

            const container = await createContent({
                variables: {
                    data: {
                        containers: [containerData],
                    },
                },
            });

            container && message.success("SAVED!");
        }
    };

    const handleUpdatePage = async (values) => {
        const brandInfo = pageDetails.payload;

        const containerData = {
            page_id: fieldInfo.pageId,
            name: `Privacy Policy Container - ${brandInfo.brandName}`,
            order: 0,
            field_collections: [
                {
                    name: "Privacy Policy FC",
                    order: 0,
                    fields: [
                        {
                            order: 0,
                            label: "privacy policy (default)",
                            name: "PRIVACY-POLICY-TITLE",
                            value: values.title ? values.title.trim() : "",
                        },
                        {
                            order: 1,
                            label: "body",
                            name: "BODY",
                            value: values.body ? values.body.trim() : "",
                        },
                    ],
                },
            ],
        };

        const container = await createContent({
            variables: {
                data: {
                    containers: [containerData],
                },
            },
        });

        if (!_.isEmpty(fieldInfo.containerId)) {
            await removeMultiContainer({
                variables: {
                    data: {
                        ids: fieldInfo.containerId,
                    },
                },
            });
        }

        container && message.success("Update success.");
    };

    return (
        <Spin spinning={loader}>
            <Formik
                initialValues={initalValue}
                validationSchema={validationSchema}
                enableReinitialize={reinitialize}
                onSubmit={(values: any) => {
                    handleSubmit(values);
                }}
                render={(formikBag) => (
                    <Form
                        {...{
                            formikBag,
                        }}
                    />
                )}
            />
        </Spin>
    );
};

export default PrivacyPolicyForms;
